import { Dtos } from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../utils/error";
import { useRequest } from "../../utils/request";
import { SuretyEntityIdMap } from "../types";
import { followerFromAuthenticatedUser } from "./followerFromAuthenticatedUser";
import { followersQueryKey } from "./followers.keys";

export const useCreateAgentFollower = <T extends keyof SuretyEntityIdMap>(
	url: string,
	type: T,
	id: SuretyEntityIdMap[T],
) => {
	const { post } = useRequest();
	const queryClient = useQueryClient();
	const { triggerErrorModal } = useGlobalErrorMessageModal();
	const { user } = useAuthenticatedUser();

	const mutation = useMutation({
		mutationFn: async (agent?: Dtos.Agent.Preview.Get.Response) =>
			await post(`${url}/${agent?.id ?? user.id}`, undefined, "none"),
		onMutate: (agent?: Dtos.Agent.Preview.Get.Response) => {
			const newFollower: Dtos.Follower.Get.Response = agent
				? Dtos.Follower.fromAgentPreview(agent)
				: followerFromAuthenticatedUser(user);

			queryClient.setQueryData(
				followersQueryKey(type, id),
				(oldData: Dtos.Follower.Get.Response[] | undefined) => {
					if (!!oldData) {
						return [...oldData, newFollower];
					}
					return [newFollower];
				},
			);

			return newFollower;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: followersQueryKey(type, id) });
		},
		onError: (error: Error, _variables, newFollower) => {
			logError(error);
			triggerErrorModal(error);
			if (!!newFollower) {
				queryClient.setQueryData(
					followersQueryKey(type, id),
					(oldData: Dtos.Follower.Get.Response[] | undefined) => {
						if (!!oldData) {
							return oldData.filter((follower) => follower.id !== newFollower.id);
						}
						return [];
					},
				);
			}
		},
	});

	return { createAgentFollower: mutation.mutate, createAgentFollowerLoading: mutation.isPending };
};
